import { format } from 'date-fns';
import { Ticket } from '../types/ticket';
import { ParkingLocation } from '../types/parkingLocation';
import {
	getTicketTypeMeta as getDefaultTicketTypeMeta,
	TicketTypeMeta,
} from './getTicketTypeMeta';

// Mirrors the session-age cap in getNextViolationTicketType — keep these in sync.
const SESSION_MAX_MINUTES = 12 * 60;

/**
 * Builds a short caption describing the parking-session timing for hourly
 * (time-based) lots, to surface the time limit on every scan in the ticket
 * form. Returns null for non-hourly lots or misconfigured locations.
 */
export const getHourlyTimeContext = ({
	parkingLocation,
	tickets,
	now = new Date(),
	getTicketTypeMeta = getDefaultTicketTypeMeta,
}: {
	parkingLocation: ParkingLocation;
	tickets: Ticket[];
	now?: Date;
	// Terminology getter (from useTicketTypeMeta) so the caption reflects any
	// per-tenant relabeling. Defaults to the built-in Record/Warning/Tow.
	getTicketTypeMeta?: (ticketType: string) => TicketTypeMeta;
}): string | null => {
	if (parkingLocation.violationCheckType !== 'hourlyExpiry') return null;
	const limitMinutes = parkingLocation.maxStayMinutes || 0;
	if (limitMinutes <= 0) return null;

	const limitText = formatDuration(limitMinutes);

	const lastTicket = tickets
		.filter((t) => !t.isVoid)
		.slice()
		.sort(
			(a, b) =>
				new Date(b.date).getTime() - new Date(a.date).getTime(),
		)[0];

	if (!lastTicket) {
		return `First scan — starting ${limitText} timer`;
	}

	const lastDate = new Date(lastTicket.date);
	const elapsedMinutes = (now.getTime() - lastDate.getTime()) / 60000;

	if (elapsedMinutes > SESSION_MAX_MINUTES) {
		return `First scan — starting ${limitText} timer`;
	}

	const startedAt = format(lastDate, 'h:mm a');
	// Use the (overridable) short label so the caption matches the tenant's
	// terminology, e.g. "Warning saved at …" / "Ticket saved at …".
	const { shortLabel } = getTicketTypeMeta(lastTicket.ticketType);

	if (lastTicket.ticketType === 'log') {
		if (elapsedMinutes <= limitMinutes) {
			const minutesRemaining = Math.max(
				0,
				Math.round(limitMinutes - elapsedMinutes),
			);
			return `${shortLabel} saved at ${startedAt} • ${formatDuration(
				minutesRemaining,
			)} left of ${limitText}`;
		}
		const overByMinutes = Math.max(
			1,
			Math.round(elapsedMinutes - limitMinutes),
		);
		return `${shortLabel} saved at ${startedAt} • ${formatDuration(
			overByMinutes,
		)} past ${limitText} limit`;
	}

	return `${shortLabel} saved at ${startedAt} — session already actioned`;
};

const formatDuration = (totalMinutes: number): string => {
	if (totalMinutes < 60) return `${totalMinutes} min`;
	const h = Math.floor(totalMinutes / 60);
	const m = totalMinutes % 60;
	if (m === 0) return `${h} h`;
	return `${h} h ${m} min`;
};
